import type { ComponentProps, FormEvent } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import type {
  FontSizeProps,
  LineHeightProps,
  ResponsiveValue,
  SpaceProps,
  WidthProps,
} from 'styled-system';
import { fontSize, lineHeight, space, width } from 'styled-system';

import type { BaseComponentProps } from '../../home-club-section/types';
import { useLocalization } from '../providers/LocaleProvider';
import type { ButtonKind, ButtonWithHTMLAttrsProps } from './Button';
import { Button } from './Button';
import { HeadlineH8 } from './Typography';

const StyledForm = styled.form<WidthProps>`
  ${width};
`;

const UnstyledSubmitButton = styled(Button)<
  ButtonWithHTMLAttrsProps<
    WidthProps &
      SpaceProps &
      FontSizeProps &
      LineHeightProps &
      Pick<BaseComponentProps, 'trackingProps'>
  >
>`
  ${width};
  ${space};
  ${fontSize};
  ${lineHeight};
`;

const SubmitButton = (props: ComponentProps<typeof UnstyledSubmitButton>) => (
  <UnstyledSubmitButton type="submit" m="sp_0" px="sp_16" py="sp_8" {...props} />
);

interface HomeClubFormProps extends Omit<ComponentProps<'form'>, 'onSubmit'> {
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  buttonLabel: string;
  buttonKind?: ButtonKind;
  buttonWidth?: ResponsiveValue<string | number>;
  isLoading?: boolean;
  isDisabled?: boolean;
  trackingProps?: any;
  eventName?: string;
  placement?: string;
  isRounded?: boolean;
  children?: React.ReactNode;
}

const HomeClubForm = ({
  onSubmit,
  buttonLabel,
  buttonKind = 'primary',
  buttonWidth,
  isLoading = false,
  isDisabled = false,
  trackingProps,
  eventName,
  placement,
  isRounded = true,
}: HomeClubFormProps) => {
  const { t } = useLocalization();

  return (
    <StyledForm aria-label={t('h24_hc_form_aria_label')} width={1} noValidate onSubmit={onSubmit}>
      <SubmitButton
        kind={buttonKind}
        isLoading={isLoading}
        disabled={isDisabled}
        tracking={{
          text: buttonLabel,
          placement,
          eventName,
          ...trackingProps,
        }}
        isRounded={isRounded}
        {...(buttonWidth && { width: buttonWidth })}
      >
        <HeadlineH8 color="sys.neutral.text.inverted">{buttonLabel}</HeadlineH8>
      </SubmitButton>
    </StyledForm>
  );
};

export default HomeClubForm;
